import {
  $tableMenu,
  type Command,
  type TableCellAlign,
  type TableMenuState,
} from '@bangle.io/prosemirror-plugins';
import {
  Button,
  Separator,
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@bangle.io/ui-components';
import { useAtomValue } from 'jotai';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  RemoveFormatting,
} from 'lucide-react';
import React from 'react';
import type { PmEditorService } from '../pm-editor-service';
import { useEditorCoreServices } from '../use-editor-core-services';
import {
  isTableActionAvailable,
  TABLE_ACTIONS,
  type TableAction,
} from './table-actions';
import {
  FLOATING_INITIAL_STYLE,
  useFloatingPosition,
} from './use-floating-position';

type EditorView = NonNullable<ReturnType<PmEditorService['getEditor']>>;
type Extensions = PmEditorService['extensions'];

/**
 * Floating toolbar for the table the cursor is in. Add/remove row and column
 * actions are one-click; column alignment uses pressed toggles; delete table
 * is a separate destructive control. Mutations go through table commands.
 */
export function TableMenu({ editorName }: { editorName: string }) {
  const tableMenus = useAtomValue($tableMenu);
  const { editorEngine } = useEditorCoreServices();
  const editorView = editorEngine.getEditor(editorName);
  const tableMenu = editorView ? tableMenus.get(editorView) : undefined;

  if (!editorView || !tableMenu?.show) {
    return null;
  }

  return (
    <TableMenuContent
      anchorEl={tableMenu.anchorEl}
      editorView={editorView}
      ext={editorEngine.extensions}
      key={tableMenu.tablePos}
    />
  );
}

function TableMenuContent({
  anchorEl,
  editorView,
  ext,
}: {
  anchorEl: TableMenuState['anchorEl'];
  editorView: EditorView;
  ext: Extensions;
}) {
  const triggerRef = useFloatingPosition({
    show: true,
    anchorEl,
    boundaryElement: editorView.dom,
    placement: 'top-start',
  });

  const run = (command: Command) => {
    command(editorView.state, editorView.dispatch, editorView);
    editorView.focus();
  };

  const runAction = (action: TableAction) => {
    run(action.command(ext));
  };

  const activeCell = ext.table.query.activeTableCell(editorView.state);
  const align: TableCellAlign | 'none' = activeCell?.align ?? 'none';
  const insertActions = TABLE_ACTIONS.filter(
    (action) => action.section === 'insert',
  );
  const deleteRow = TABLE_ACTIONS.find((a) => a.id === 'delete-row');
  const deleteColumn = TABLE_ACTIONS.find((a) => a.id === 'delete-column');
  const deleteTable = TABLE_ACTIONS.find((a) => a.id === 'delete-table');

  const dismiss = () => {
    run(ext.tableMenu.command.dismissTableMenu());
  };

  const setAlign = (next: TableCellAlign | null) => {
    run(ext.table.command.setColumnAlign(next));
  };

  return (
    <div ref={triggerRef} style={FLOATING_INITIAL_STYLE}>
      <div
        aria-label={t.app.editor.tableMenu.label}
        className="flex flex-wrap items-center gap-0.5 rounded-md border bg-popover p-0.5 shadow-xs"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            dismiss();
          }
        }}
        role="toolbar"
      >
        <TooltipProvider delay={300}>
          {insertActions.map((action) => (
            <ActionButton
              key={action.id}
              action={action}
              disabled={!isTableActionAvailable(action, ext, editorView.state)}
              onClick={() => runAction(action)}
            />
          ))}

          <Separator className="mx-0.5 h-6" orientation="vertical" />

          {deleteRow && (
            <ActionButton
              action={deleteRow}
              className="text-destructive hover:text-destructive"
              disabled={
                !isTableActionAvailable(deleteRow, ext, editorView.state)
              }
              onClick={() => runAction(deleteRow)}
            />
          )}
          {deleteColumn && (
            <ActionButton
              action={deleteColumn}
              className="text-destructive hover:text-destructive"
              disabled={
                !isTableActionAvailable(deleteColumn, ext, editorView.state)
              }
              onClick={() => runAction(deleteColumn)}
            />
          )}

          <Separator className="mx-0.5 h-6" orientation="vertical" />

          <AlignToggle
            active={align === 'none'}
            label={t.app.editor.tableMenu.alignNone}
            onToggle={() => setAlign(null)}
          >
            <RemoveFormatting />
          </AlignToggle>
          <AlignToggle
            active={align === 'left'}
            label={t.app.editor.tableMenu.alignLeft}
            onToggle={() => setAlign('left')}
          >
            <AlignLeft />
          </AlignToggle>
          <AlignToggle
            active={align === 'center'}
            label={t.app.editor.tableMenu.alignCenter}
            onToggle={() => setAlign('center')}
          >
            <AlignCenter />
          </AlignToggle>
          <AlignToggle
            active={align === 'right'}
            label={t.app.editor.tableMenu.alignRight}
            onToggle={() => setAlign('right')}
          >
            <AlignRight />
          </AlignToggle>

          {deleteTable && (
            <>
              <Separator className="mx-0.5 h-6" orientation="vertical" />
              <ActionButton
                action={deleteTable}
                className="text-destructive hover:text-destructive"
                disabled={
                  !isTableActionAvailable(deleteTable, ext, editorView.state)
                }
                onClick={() => runAction(deleteTable)}
              />
            </>
          )}
        </TooltipProvider>
      </div>
    </div>
  );
}

function ActionButton({
  action,
  className,
  disabled,
  onClick,
}: {
  action: TableAction;
  className?: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = action.icon;
  const label = action.title();
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={label}
            className={className ? `h-8 w-8 p-0 ${className}` : 'h-8 w-8 p-0'}
            disabled={disabled}
            onClick={onClick}
            onPointerDown={(event) => event.preventDefault()}
            size="icon"
            type="button"
            variant="ghost"
          >
            <Icon />
          </Button>
        }
      />
      <TooltipContent side="bottom" className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function AlignToggle({
  active,
  children,
  label,
  onToggle,
}: {
  active: boolean;
  children: React.ReactNode;
  label: string;
  onToggle: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Toggle
            aria-label={label}
            aria-pressed={active}
            className="h-8 w-8 p-0"
            onPointerDown={(event) => event.preventDefault()}
            onPressedChange={() => onToggle()}
            pressed={active}
            size="sm"
          >
            {children}
          </Toggle>
        }
      />
      <TooltipContent side="bottom" className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
